export interface AuthUser {
  id: string;
  partyId?: string | null;
}

export interface Asset {
  symbol: string;
  balance: number;
  chain?: string;
  chainType?: string;
  chains?: Array<{ chain: string; chainType?: string }>;
  chainBalances?: Record<string, number>;
  [key: string]: unknown;
}

export interface WalletAddress {
  address: string;
  chainType: string;
  [key: string]: unknown;
}

export interface CantonContract<TPayload = Record<string, unknown>> {
  contractId: string;
  createdAt?: string;
  templateId?: string;
  payload: TPayload;
  [key: string]: unknown;
}

export interface SDKEvent {
  contractId: string;
  templateId: string;
  payload?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ConnectState {
  connected: boolean;
  user: AuthUser | null;
  network?: 'testnet' | 'mainnet';
}

export interface StratosSDK {
  connect(): Promise<ConnectState>;
  getAssets(): Promise<Asset[]>;
  getPartyId(): Promise<string>;
  getAddresses(): Promise<WalletAddress[]>;
  cantonCreate(args: Record<string, unknown>): Promise<{ contractId: string; events?: SDKEvent[] }>;
  cantonQuery<TPayload = Record<string, unknown>>(args: Record<string, unknown>): Promise<Array<CantonContract<TPayload>>>;
  cantonExercise(args: Record<string, unknown>): Promise<{ exerciseResult?: unknown; events?: SDKEvent[] }>;
  transfer(args: Record<string, unknown>): Promise<{ contractId?: string; trackingId?: string }>;
  sendContractCall(
    contractAddress: string,
    callData: string,
    chainId: number
  ): Promise<{ txHash: string; transactionHash: string; status?: string }>;
  sendEVMTransaction(args: Record<string, unknown>): Promise<{ txHash: string; transactionHash: string; status?: string }>;
  getTransactionReceipt(txHash: string, chainId: number): Promise<{ status?: string; contractAddress?: string } | null>;
  on(event: string, listener: (...args: any[]) => void): void;
  removeAllListeners(): void;
}

class MockSDK implements StratosSDK {
  private listeners = new Map<string, Set<(...args: unknown[]) => void>>();

  async connect(): Promise<ConnectState> {
    return { connected: false, user: null, network: 'testnet' };
  }

  async getAssets(): Promise<Asset[]> {
    return [];
  }

  async getPartyId(): Promise<string> {
    return 'mock-party';
  }

  async getAddresses(): Promise<WalletAddress[]> {
    return [];
  }

  async cantonCreate(): Promise<{ contractId: string; events?: SDKEvent[] }> {
    return { contractId: `mock-${crypto.randomUUID()}`, events: [] };
  }

  async cantonQuery<TPayload = Record<string, unknown>>(): Promise<Array<CantonContract<TPayload>>> {
    return [];
  }

  async cantonExercise(): Promise<{ exerciseResult?: unknown; events?: SDKEvent[] }> {
    return { exerciseResult: {}, events: [] };
  }

  async transfer(): Promise<{ contractId?: string; trackingId?: string }> {
    return { contractId: `mock-${crypto.randomUUID()}`, trackingId: `mock-${Date.now()}` };
  }

  async sendContractCall(): Promise<{ txHash: string; transactionHash: string; status?: string }> {
    const txHash = `0x${crypto.randomUUID().replace(/-/g, '')}`;
    return { txHash, transactionHash: txHash, status: 'success' };
  }

  async sendEVMTransaction(): Promise<{ txHash: string; transactionHash: string; status?: string }> {
    const txHash = `0x${crypto.randomUUID().replace(/-/g, '')}`;
    return { txHash, transactionHash: txHash, status: 'success' };
  }

  async getTransactionReceipt(): Promise<{ status?: string; contractAddress?: string } | null> {
    return { status: 'success', contractAddress: `0x${crypto.randomUUID().replace(/-/g, '').slice(0, 40)}` };
  }

  on(event: string, listener: (...args: any[]) => void): void {
    const current = this.listeners.get(event) || new Set<(...args: unknown[]) => void>();
    current.add(listener);
    this.listeners.set(event, current);
  }

  removeAllListeners(): void {
    this.listeners.clear();
  }
}

const sdk = new MockSDK();

export function getSDK(_options?: Record<string, unknown>): StratosSDK {
  return sdk;
}
